export const DEFAULT_ARTICLE_PAGE_SIZE = 10;
export const ARTICLE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200] as const;

export type PaginationSummary = {
  pageIndex: number;
  pageSize: number;
  pageCount: number;
  startIndex: number;
  endIndex: number;
  start: number;
  end: number;
  totalRows: number;
};

function toPositiveInteger(value: number, fallback: number): number {
  return Number.isInteger(value) && value > 0 ? value : fallback;
}

export function getPageCount(totalRows: number, pageSize: number): number {
  const safeTotal = Math.max(Math.trunc(totalRows), 0);
  const safePageSize = toPositiveInteger(Math.trunc(pageSize), DEFAULT_ARTICLE_PAGE_SIZE);
  return Math.max(Math.ceil(safeTotal / safePageSize), 1);
}

export function clampPageIndex(pageIndex: number, totalRows: number, pageSize: number): number {
  const safePageIndex = Number.isInteger(pageIndex) ? pageIndex : 0;
  const maxPageIndex = getPageCount(totalRows, pageSize) - 1;
  return Math.min(Math.max(safePageIndex, 0), maxPageIndex);
}

export function getPaginationSummary(totalRows: number, pageIndex: number, pageSize: number): PaginationSummary {
  const safeTotal = Math.max(Math.trunc(totalRows), 0);
  const safePageSize = toPositiveInteger(Math.trunc(pageSize), DEFAULT_ARTICLE_PAGE_SIZE);
  const safePageIndex = clampPageIndex(pageIndex, safeTotal, safePageSize);
  const startIndex = safeTotal === 0 ? 0 : safePageIndex * safePageSize;
  const endIndex = safeTotal === 0 ? 0 : Math.min(startIndex + safePageSize, safeTotal);

  return {
    pageIndex: safePageIndex,
    pageSize: safePageSize,
    pageCount: getPageCount(safeTotal, safePageSize),
    startIndex,
    endIndex,
    start: safeTotal === 0 ? 0 : startIndex + 1,
    end: endIndex,
    totalRows: safeTotal
  };
}

export function paginateRows<T>(rows: T[], pageIndex: number, pageSize: number): PaginationSummary & { items: T[] } {
  const summary = getPaginationSummary(rows.length, pageIndex, pageSize);
  return {
    ...summary,
    items: rows.slice(summary.startIndex, summary.endIndex)
  };
}
